import { useMemo, useState } from "react";
import { BookOpen } from "lucide-react";
import { Panel, Result, Cta, Segmented, field, label, fmt } from "./_ui";

const STOCKS = [
  { key: "60u", label: "60# uncoated text (90 gsm)", caliper: 0.0045 },
  { key: "70u", label: "70# uncoated text (105 gsm)", caliper: 0.0055 },
  { key: "80g", label: "80# gloss text (120 gsm)", caliper: 0.0034 },
  { key: "100g", label: "100# gloss text (150 gsm)", caliper: 0.0045 },
  { key: "80m", label: "80# silk text (120 gsm)", caliper: 0.0043 },
  { key: "100m", label: "100# silk text (150 gsm)", caliper: 0.0055 },
  { key: "80c", label: "80# cover (216 gsm)", caliper: 0.0085 },
];

export function BookletPlanner() {
  const [pages, setPages] = useState(30);
  const [stock, setStock] = useState("100g");
  const [cover, setCover] = useState("self");

  const r = useMemo(() => {
    const s = STOCKS.find((x) => x.key === stock);
    const interior = cover === "self" ? pages : pages;
    const rounded = Math.max(8, Math.ceil(interior / 4) * 4);
    const sheets = rounded / 4;
    const thickness = (rounded / 2) * s.caliper + (cover === "plus" ? 2 * 0.0085 : 0);
    const saddleMax = s.caliper <= 0.0045 ? 92 : s.caliper <= 0.0055 ? 72 : 48;
    let binding, why;
    if (rounded <= 8) { binding = "Folded sheet / saddle stitch"; why = "8 pages is a single folded sheet — stitch optional."; }
    else if (rounded <= saddleMax) { binding = "Saddle stitch"; why = `Up to about ${saddleMax} pages on this stock before creep and bulk at the spine become a problem.`; }
    else if (thickness >= 0.125 && thickness <= 2.5) { binding = "Perfect bound (PUR)"; why = `Spine of ${fmt(thickness, 3)}" — thick enough to glue (min 1/8") and print a spine title.`; }
    else { binding = "Coil / Wire-O or hardcover"; why = thickness > 2.5 ? "Too thick for perfect binding — split into volumes or case-bind." : "Too thin to glue — use saddle stitch."; }
    const creep = rounded > 32 ? `${fmt((rounded / 4) * s.caliper, 3)}" total — inside pages push out; we compensate automatically` : "negligible";
    return { rounded, sheets, thickness, binding, why, creep, pad: rounded !== pages };
  }, [pages, stock, cover]);

  return (
    <Panel icon={BookOpen} title="Booklet page-count & binding planner" testId="booklet-planner">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><label className={label}>Page count (incl. cover)</label><input type="number" min="4" step="1" value={pages} onChange={(e) => setPages(Math.max(4, +e.target.value || 4))} className={field} data-testid="booklet-pages" /></div>
        <div className="col-span-2"><label className={label}>Interior paper</label><select value={stock} onChange={(e) => setStock(e.target.value)} className={field} data-testid="booklet-stock">{STOCKS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select></div>
        <div><label className={label}>Cover</label><Segmented value={cover} onChange={setCover} options={[{ value: "self", label: "Self cover" }, { value: "plus", label: "Plus cover" }]} testId="booklet-cover" /></div>
      </div>
      <div className="grid sm:grid-cols-4 gap-3 mt-5">
        <Result title="Print as" value={`${r.rounded} pages`} sub={r.pad ? `${pages} rounds up to a multiple of 4 — add ${r.rounded - pages} blank or "notes" page${r.rounded - pages > 1 ? "s" : ""}` : "already a multiple of 4"} testId="booklet-result-pages" tone={r.pad ? "warn" : "good"} />
        <Result title="Sheets" value={`${r.sheets}`} sub="folded sheets (4 pages each)" testId="booklet-result-sheets" />
        <Result title="Recommended binding" value={r.binding} sub={r.why} testId="booklet-result-binding" />
        <Result title="Spine thickness" value={`${fmt(r.thickness, 3)}"`} sub={`creep: ${r.creep}`} testId="booklet-result-spine" />
      </div>
      <p className="text-xs text-white/70 mt-4">Supply pages as single pages in reading order (not printer spreads). Saddle-stitched books need a page count divisible by 4; perfect-bound books only need an even count, but 4-up saves money. Cover files: for perfect binding, use the spine width above in a single wrap-around cover spread.</p>
      <div className="flex flex-wrap gap-2 mt-4">
        <Cta to="/shop/booklets-books" testId="booklet-cta-shop">Order booklets & books</Cta>
        <Cta to="/blog/saddle-stitch-smyth-sewn-pur-perfect-binding" outline testId="booklet-cta-guide">Binding methods explained</Cta>
        <Cta to="#spine-calculator" outline testId="booklet-cta-spine">Hardcover spine calculator</Cta>
      </div>
    </Panel>
  );
}
